import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { Input } from '../../../components/input'
import { Select } from '../../../components/select'
import { Textarea } from '../../../components/textarea'
import { Checkbox, CheckboxField, CheckboxGroup } from '../../../components/checkbox'
import { MultiSelect } from '../../../components/multi-select'
import { ErrorDialog, SuccessDialog } from '../../../components/custom/ResponseDialog'
import loadingImage from '../../../assets/images/boat-loading.gif'
import {
    GET_LOCATIONS,
    GET_STATION_DISTANCE_CONFIGS,
    STATION_DISTANCE_STATUS,
} from 'graphql/stationDistanceConfig/queries'
import { CREATE_STATION_DISTANCE_CONFIG, UPDATE_STATION_DISTANCE_CONFIG } from 'graphql/stationDistanceConfig/mutations'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'

const StationDistanceConfigForms = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = !!id

    const [formData, setFormData] = useState({
        distance_nm: 0,
        effective_from: '',
        effective_to: '',
        from_station_id: '',
        to_station_id: '',
        status_code: '',
    })

    const [errors, setErrors] = useState({})
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [blockSubmission, setBlockSubmission] = useState(false)

    const [countries, setCountries] = useState([])
    const [locations, setLocations] = useState([])
    const [statusCodes, setStatusCodes] = useState([])

    const [fromStationCountryFilter, setFromStationCountryFilter] = useState('')
    const [toStationCountryFilter, setToStationCountryFilter] = useState('')
    const [loadingStatus, setLoadingStatus] = useState(true)

    const { data: allCountries } = useQuery(GET_COUNTRY_LIST, {
        variables: {},
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
    })

    const { data: allLocations } = useQuery(GET_LOCATIONS, {
        variables: {
            page: { limit: 100, offset: 0 },
        },
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.searchLocation?.locations) {
                setLocations(data.searchLocation.locations)
            }
        },
    })

    const { data: statusData, loading: statusLoading } = useQuery(STATION_DISTANCE_STATUS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.stationDistanceStatus) {
                setStatusCodes(data.stationDistanceStatus)
            }
        },
    })

    const [getStationDistanceConfigDetails, { loading: loadingSDC }] = useLazyQuery(GET_STATION_DISTANCE_CONFIGS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (
                data?.getStationDistanceConfigs?.station_distance_configs &&
                data.getStationDistanceConfigs.station_distance_configs.length > 0 &&
                data.getStationDistanceConfigs.station_distance_configs[0]
            ) {
                const stationDistanceConfigData = data.getStationDistanceConfigs.station_distance_configs[0]

                if (stationDistanceConfigData.from_station?.country_details?.country_code)
                    setFromStationCountryFilter(stationDistanceConfigData.from_station?.country_details?.country_code)

                if (stationDistanceConfigData.to_station?.country_details?.country_code)
                    setToStationCountryFilter(stationDistanceConfigData.to_station?.country_details?.country_code)

                setFormData({
                    distance_nm: stationDistanceConfigData.distance_nm,
                    effective_from: stationDistanceConfigData.effective_from,
                    effective_to: stationDistanceConfigData.effective_to,
                    from_station_id: stationDistanceConfigData.from_station_id,
                    to_station_id: stationDistanceConfigData.to_station_id,
                    status_code: stationDistanceConfigData.status_code,
                })
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch station distance config details'))
            setShowErrDialog(true)
        },
    })

    const [createStationDistanceConfig, { loading: createLoading }] = useMutation(CREATE_STATION_DISTANCE_CONFIG, {
        onCompleted: data => {
            if (data.createStationDistanceConfig.success) {
                setSuccessMessage(t('Station distance configuration created successfully'))
                setShowSucDialog(true)
                setBlockSubmission(true)
                setTimeout(() => {
                    navigate('/nautical-miles-distance')
                }, 2000)
            } else {
                setErrorMessage(
                    data.createStationDistanceConfig.message || t('Failed to create station distance configuration')
                )
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while creating station distance configuration'))
            setShowErrDialog(true)
        },
    })

    const [updateStationDistanceConfig, { loading: updateLoading }] = useMutation(UPDATE_STATION_DISTANCE_CONFIG, {
        onCompleted: data => {
            if (data.updateStationDistanceConfig.success) {
                setSuccessMessage(t('station distance config updated successfully'))
                setShowSucDialog(true)
                setBlockSubmission(true)
                setTimeout(() => {
                    navigate('/nautical-miles-distance')
                }, 2000)
            } else {
                setErrorMessage(
                    data.updateStationDistanceConfig.message || t('Failed to update station distance config')
                )
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while updating station distance config'))
            setShowErrDialog(true)
        },
    })

    useEffect(() => {
        // Load station distance config details if in edit mode
        if (isEditMode && id) {
            getStationDistanceConfigDetails({
                variables: {
                    filter: { station_distance_id: id },
                    page: { offset: 0, limit: 1 },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Helper function to remove __typename from objects
    const removeTypename = obj => {
        if (obj === null || obj === undefined) return obj
        if (Array.isArray(obj)) return obj.map(removeTypename)
        if (typeof obj === 'object') {
            const { __typename, ...rest } = obj
            return Object.keys(rest).reduce((acc, key) => {
                acc[key] = removeTypename(rest[key])
                return acc
            }, {})
        }
        return obj
    }

    const validateForm = () => {
        const newErrors = {}

        const distance_nm_number = Number(formData.distance_nm)
        if (!distance_nm_number || distance_nm_number <= 0) {
            newErrors.distance_nm = t('Valid distance is required')
        }

        if (!formData.effective_from) newErrors.effective_from = t('Effective from date is required')
        if (!formData.effective_to) newErrors.effective_to = t('Effective to date is required')

        if (formData.effective_from && formData.effective_to && formData.effective_from >= formData.effective_to) {
            newErrors.effective_to = t('Effective to date must be after Effective from date')
        }

        if (formData.from_station_id && formData.to_station_id && formData.from_station_id === formData.to_station_id) {
            newErrors.to_station_id = t('Station from must not be the same as Station to')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm() || blockSubmission) {
            return
        }

        // Clean the data by removing __typename fields
        const cleanedData = removeTypename(formData)

        const station_distance_config = {
            ...formData,
            distance_nm: Number(formData.distance_nm),
            is_bidirectional: false,
        }

        if (isEditMode) {
            updateStationDistanceConfig({
                variables: { station_distance_id: id, station_distance_config: station_distance_config },
            })
        } else {
            createStationDistanceConfig({ variables: { station_distance_config: station_distance_config } })
        }
    }

    const filteredStationsFrom = locations.filter(v => v.country_details?.country_code === fromStationCountryFilter)
    const filteredStationsTo = locations.filter(v => v.country_details?.country_code === toStationCountryFilter)

    if (loadingSDC) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/nautical-miles-distance')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Station Distance Configs')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                                        {isEditMode
                                            ? t('Edit Station Distance Config')
                                            : t('Create Station Distance Config')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {isEditMode
                                            ? t('Update the Station Distance Configuration details')
                                            : t('Add a new Station Distance Configuration details')}
                                    </p>
                                </div>
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900">{t('Basic Information')}</h3>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Distance NM')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        step="1.00"
                                        value={formData.distance_nm}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                distance_nm: e.target.value < 0.001 ? 0.001 : e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                    {errors.distance_nm && (
                                        <p className="text-red text-xs mt-1">{errors.distance_nm}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.status_code}
                                        onChange={e => setFormData({ ...formData, status_code: e.target.value })}
                                        disabled={statusLoading}
                                        required
                                    >
                                        <option value="" disabled>
                                            {statusLoading ? t('Loading...') : t('Select Status')}
                                        </option>

                                        {!statusLoading &&
                                            statusCodes.map(v => (
                                                <option key={v.code} value={v.code}>
                                                    {t(v.description?.en ?? v.code)}
                                                </option>
                                            ))}
                                    </Select>
                                </div>
                                {/* Effective Dates */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Effective Dates')}</h3>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Effective From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="date"
                                        value={formData.effective_from}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                effective_from: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                    {errors.effective_from && (
                                        <p className="text-red text-xs mt-1">{errors.effective_from}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Effective To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="date"
                                        value={formData.effective_to}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                effective_to: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                    {errors.effective_to && (
                                        <p className="text-red text-xs mt-1">{errors.effective_to}</p>
                                    )}
                                </div>
                                {/* Stations */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Stations')}</h3>
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Station From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div style={{ display: 'flex' }}>
                                        <Select
                                            value={fromStationCountryFilter}
                                            onChange={e => {
                                                setFromStationCountryFilter(e.target.value)
                                                setFormData({
                                                    ...formData,
                                                    from_station_id: '',
                                                })
                                            }}
                                            required
                                        >
                                            <option value="" disabled>
                                                {t('Select Country')}
                                            </option>
                                            {countries.map(v => (
                                                <option value={v.alpha_iso2}>{t(v.country ?? v.alpha_iso2)}</option>
                                            ))}
                                        </Select>
                                        <Select
                                            value={formData.from_station_id}
                                            disabled={filteredStationsFrom.length <= 0}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    from_station_id: e.target.value,
                                                })
                                            }
                                            required
                                        >
                                            <option value="" disabled>
                                                {t(
                                                    fromStationCountryFilter === ''
                                                        ? '<---'
                                                        : filteredStationsFrom.length <= 0
                                                          ? 'No Stations Found'
                                                          : 'Select Station'
                                                )}
                                            </option>
                                            {filteredStationsFrom.map(v => (
                                                <option value={v.id}>{t(v.name.en ?? v.id)}</option>
                                            ))}
                                        </Select>
                                    </div>
                                    {errors.from_station_id && (
                                        <p className="text-red text-xs mt-1">{errors.from_station_id}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Station To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div style={{ display: 'flex' }}>
                                        <Select
                                            value={toStationCountryFilter}
                                            onChange={e => {
                                                setToStationCountryFilter(e.target.value)
                                                setFormData({
                                                    ...formData,
                                                    to_station_id: '',
                                                })
                                            }}
                                            required
                                        >
                                            <option value="" disabled>
                                                {t('Select Country')}
                                            </option>
                                            {countries.map(v => (
                                                <option value={v.alpha_iso2}>{t(v.country ?? v.alpha_iso2)}</option>
                                            ))}
                                        </Select>
                                        <Select
                                            value={formData.to_station_id}
                                            disabled={filteredStationsTo.length <= 0}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    to_station_id: e.target.value,
                                                })
                                            }
                                            required
                                        >
                                            <option value="" disabled>
                                                {t(
                                                    toStationCountryFilter === ''
                                                        ? '<---'
                                                        : filteredStationsTo.length <= 0
                                                          ? 'No Stations Found'
                                                          : 'Select Station'
                                                )}
                                            </option>
                                            {filteredStationsTo.map(v => (
                                                <option value={v.id}>{t(v.name.en ?? v.id)}</option>
                                            ))}
                                        </Select>
                                    </div>
                                    {errors.to_station_id && (
                                        <p className="text-red text-xs mt-1">{errors.to_station_id}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button
                                type="button"
                                onClick={() => {
                                    if (!blockSubmission) navigate('/nautical-miles-distance')
                                }}
                                className="text-sm font-semibold leading-6 text-gray-900"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading || updateLoading}
                                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                            >
                                {createLoading || updateLoading
                                    ? t('Saving...')
                                    : isEditMode
                                      ? t('Update Station Distance Config')
                                      : t('Create Station Distance Config')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {showSucDialog && (
                <SuccessDialog
                    status={showSucDialog}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }}
                />
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={showErrDialog}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={() => {
                        setShowErrDialog(false)
                        setErrorMessage('')
                    }}
                />
            )}
        </>
    )
}

export default StationDistanceConfigForms
